<script>
/**
 * @component Validation
 * @description Pantalla de validación de prestaciones médicas. Flujo doctor_ui.
 * El prestador es siempre el doctor logueado — el backend lo toma del token de sesión.
 * Capas: elegibilidad del afiliado → cobertura del plan → habilitación del prestador → valorización.
 */
import memberService from "@/services/member";
import validationService from "@/services/validation";

const REJECTION_LABELS = {
  member_inactive: "El afiliado no está activo",
  member_no_plan: "El afiliado no tiene un plan activo",
  invalid_token: "Token inválido",
  practice_not_covered: "La prestación no está cubierta por el plan",
  provider_inactive: "El prestador no está activo",
  provider_no_contract:
    "El prestador no tiene contrato vigente para esta prestación",
  // Motivos que devuelven los motores de elegibilidad externos.
  member_not_verified: "No pudimos verificar la identidad del afiliado",
  member_delinquent: "El afiliado tiene su cuenta en mora",
  eligibility_engine_down: "El servicio de validación no está disponible",
};

// UC-REG-165 — motivos por los que una prestación aparece en el bucket
// "No cubiertas para este plan" del dropdown.
const NOT_COVERED_REASONS = {
  plan_not_covered: "El plan no cubre esta prestación",
  contract_no_item_for_plan: "Tu contrato no la incluye para este plan",
};

export default {
  name: "Validation",
  data() {
    return {
      // Búsqueda de afiliado
      searchInput: "",
      searchingPatient: false,
      patientFound: null,
      patientSelected: null,
      errorPatient: null,

      // Selección de prestación.
      // UC-REG-165 — practiceGroups particiona por cobertura del plan del
      // afiliado seleccionado en el paso 1. Se trae completo al elegir el
      // afiliado y el filtrado del dropdown es local (ver filterPractices).
      practiceId: "",
      practiceGroups: { covered: [], notCovered: [] },
      practiceQuery: "",
      practiceSearchLoading: false,
      // UC-REG-165 — Forzar que el popper del dropdown SIEMPRE abra hacia
      // abajo (deshabilitar el flip de Popper.js). Sin esto, cuando el
      // input queda cerca del fondo del viewport, se abre hacia arriba y
      // se ve muy feo. Combinado con el scroll del wrapper en
      // onPracticeDropdownVisibleChange asegura que haya espacio.
      practicePopperOptions: {
        modifiers: {
          flip: { enabled: false },
          preventOverflow: { enabled: false },
        },
      },

      // Token
      token: "",

      // Resultado
      validatingCode: false,
      validationResult: null,
      errorCode: null,
    };
  },
  computed: {
    /** @returns {number} Paso activo del stepper (1, 2 o 3) */
    currentStep() {
      if (!this.patientSelected) return 1;
      if (!this.practiceId) return 2;
      return 3;
    },
    /** @returns {string} Iniciales del afiliado seleccionado */
    patientInitials() {
      if (!this.patientSelected) return "";
      return (
        (this.patientSelected.firstName || "")[0] +
        (this.patientSelected.lastName || "")[0]
      ).toUpperCase();
    },
    /** @returns {string} Nombre completo del afiliado seleccionado */
    patientName() {
      if (!this.patientSelected) return "";
      return `${this.patientSelected.lastName}, ${this.patientSelected.firstName}`;
    },
    /** @returns {string} Resumen del afiliado seleccionado */
    patientSummary() {
      if (!this.patientSelected) return "";
      const parts = [];
      if (this.patientSelected.dni)
        parts.push(`DNI ${this.patientSelected.dni}`);
      if (this.patientSelected.memberNumber)
        parts.push(`Afil. ${this.patientSelected.memberNumber}`);
      const planName = this.patientSelected.plan?.name || "";
      if (planName) parts.push(planName);
      return parts.join(" · ");
    },
    /** @returns {string[]} Motivos de rechazo con label legible */
    rejectionLabels() {
      if (!this.validationResult || !this.validationResult.rejectionReasons)
        return [];
      return this.validationResult.rejectionReasons.map(
        (r) => REJECTION_LABELS[r] || r
      );
    },
    /**
     * @returns {{covered: Array, notCovered: Array}} Prestaciones que pasan el
     *   filtro tipeado en el dropdown. El filtrado es local: la lista completa
     *   del afiliado ya está en memoria desde que se lo seleccionó.
     */
    visiblePracticeGroups() {
      const q = this.practiceQuery.trim().toLowerCase();
      if (!q) return this.practiceGroups;
      // Se matchea contra todo lo que la fila muestra — código y denominación
      // del prestador y del financiador — para que se pueda buscar por
      // cualquiera de los dos nomencladores, igual que hacía el backend.
      const matches = (o) =>
        [o.providerCode, o.providerName, o.financiadorCode, o.financiadorName]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(q));
      return {
        covered: this.practiceGroups.covered.filter(matches),
        notCovered: this.practiceGroups.notCovered.filter(matches),
      };
    },
  },
  methods: {
    /**
     * @method searchPatient
     * @desc Busca afiliados por Nº de afiliado o DNI
     */
    async searchPatient() {
      if (!this.searchInput.trim()) {
        this.errorPatient = "Ingresá un Nº de afiliado o DNI";
        return;
      }
      this.searchingPatient = true;
      this.errorPatient = null;
      this.patientFound = null;
      try {
        const res = await memberService.query({
          searchInput: this.searchInput.trim(),
          limit: 10,
        });
        const docs = res.docs || [];
        if (docs.length > 0) {
          this.patientFound = docs;
        } else {
          this.errorPatient = "No se encontró ningún afiliado con esos datos";
        }
      } catch {
        this.errorPatient = "Error al buscar el afiliado";
      } finally {
        this.searchingPatient = false;
      }
    },

    /**
     * @method selectPatient
     * @desc Selecciona un afiliado de la lista y avanza al paso 2
     * @param {Object} patient
     */
    selectPatient(patient) {
      this.patientSelected = patient;
      this.patientFound = null;
      // Las prestaciones dependen del plan del afiliado, así que se traen
      // al elegirlo: el dropdown ya abre con la lista lista y, si hay una
      // sola cubierta, queda preseleccionada.
      this.practiceId = "";
      this.practiceQuery = "";
      this.loadPractices();
    },

    /**
     * @method buildPracticeOption
     * @desc Arma el shape para el dropdown a partir del doc del backend.
     *
     *       UC-REG-165 — en el validador la nomenclatura del prestador va
     *       arriba grande (código y denominación), y la del financiador
     *       chica debajo con label "FINANC." — el prestador tiene sus
     *       propios códigos en la cabeza, no los del financiador. Esto
     *       aplica solo al validador; en el ABM de contratos y resto de la
     *       UI se mantiene el `codeDisplayMode` del contrato.
     *
     *       Cuando el item del contrato NO tiene `providerCode` ni
     *       `providerName` cargados, se completa silenciosamente con la
     *       nomenclatura del financiador — una sola línea, sin label.
     *
     * @param {Object} p - Doc del endpoint /validations/my-practices
     * @param {boolean} [disabled=false]
     * @param {string} [disabledReason]
     * @returns {Object} Option para el-select
     */
    buildPracticeOption(p, disabled = false, disabledReason = "") {
      const hasProvider = !!(p.providerCode || p.providerName);
      // Fallback silencioso a la nomenclatura del financiador cuando el
      // ítem del contrato no tiene providerCode/Name cargado. Los dos
      // códigos van a mostrar el mismo número — comportamiento esperado.
      const providerCode = p.providerCode || p.code;
      const providerName = p.providerName || p.name;
      return {
        value: p._id,
        label: `${providerCode} - ${providerName}`,
        hasProvider,
        providerCode,
        providerName,
        financiadorCode: p.code,
        financiadorName: p.name,
        disabled,
        disabledReason: disabled ? disabledReason : "",
      };
    },

    /**
     * @method onPracticeDropdownVisibleChange
     * @desc Handler del `@visible-change` del el-select de prestaciones.
     *       Hace scroll del input al top del viewport para que el popper del
     *       dropdown siempre tenga espacio para abrir hacia abajo (evita el
     *       flip para arriba de Popper.js cuando el input queda cerca del
     *       fondo). Al cerrarse limpia el filtro tipeado.
     * @param {boolean} v - true cuando el dropdown se abre
     */
    onPracticeDropdownVisibleChange(v) {
      if (!v) {
        this.practiceQuery = "";
        return;
      }
      // Scroll del wrapper del select al top del viewport para asegurar
      // espacio abajo. `$refs.practiceSelect.$el` es el div raíz del
      // el-select — más confiable que buscar el <input> interno.
      this.$nextTick(() => {
        const wrap = this.$refs.practiceSelect && this.$refs.practiceSelect.$el;
        if (wrap && typeof wrap.scrollIntoView === "function") {
          wrap.scrollIntoView({ block: "start", behavior: "smooth" });
        }
      });
    },

    /**
     * @method loadPractices
     * @desc Trae TODAS las prestaciones del contrato activo del prestador para
     *       el afiliado elegido, y preselecciona si hay una sola cubierta.
     *       UC-REG-165 — con `memberId` el backend devuelve
     *       `{ covered, notCovered }`; sin afiliado cae al shape legacy
     *       `{ docs, total }`. El filtrado posterior es local.
     */
    async loadPractices() {
      this.practiceSearchLoading = true;
      try {
        const memberId = this.patientSelected?._id || null;
        const res = await validationService.getMyPractices("", memberId);

        if (memberId && (res.covered || res.notCovered)) {
          this.practiceGroups = {
            covered: (res.covered || []).map((p) =>
              this.buildPracticeOption(p, false)
            ),
            notCovered: (res.notCovered || []).map((p) =>
              this.buildPracticeOption(
                p,
                true,
                NOT_COVERED_REASONS[p.reason] || "No cubierta para este plan"
              )
            ),
          };
        } else {
          // Legacy — sin memberId (defensivo; en este flujo siempre hay uno).
          const flat = (res.docs || []).map((p) => this.buildPracticeOption(p));
          this.practiceGroups = { covered: flat, notCovered: [] };
        }

        // Una sola prestación cubierta: se preselecciona en vez de hacerlo
        // abrir un dropdown de un solo ítem. En $nextTick porque el-select
        // resuelve la etiqueta desde sus <el-option> hijos, y si el valor se
        // setea antes de que se rendericen muestra el ObjectId crudo.
        const { covered } = this.practiceGroups;
        if (covered.length === 1) {
          this.$nextTick(() => {
            this.practiceId = covered[0].value;
          });
        }
      } finally {
        this.practiceSearchLoading = false;
      }
    },

    /**
     * @method filterPractices
     * @desc `filter-method` del el-select. Solo guarda lo tipeado — el
     *       filtrado lo resuelve `visiblePracticeGroups`, sin ir al backend.
     * @param {string} query
     */
    filterPractices(query) {
      this.practiceQuery = query;
    },

    /**
     * @method validateCode
     * @desc Llama al motor de validación del backend y muestra el resultado por capas
     */
    async validateCode() {
      if (!this.token.trim()) {
        this.errorCode = "Ingresá el token del afiliado";
        return;
      }
      if (!this.patientSelected?._id) {
        this.errorCode = "Seleccioná un afiliado";
        return;
      }
      if (!this.practiceId) {
        this.errorCode = "Seleccioná una prestación";
        return;
      }

      this.validatingCode = true;
      this.errorCode = null;
      this.validationResult = null;

      try {
        this.validationResult = await validationService.validate(
          this.patientSelected._id,
          this.practiceId,
          this.token
        );
      } catch {
        this.errorCode = "Error al procesar la validación";
      } finally {
        this.validatingCode = false;
      }
    },

    /** @method clearAll - Limpia todo el formulario */
    clearAll() {
      this.searchInput = "";
      this.patientFound = null;
      this.patientSelected = null;
      this.errorPatient = null;
      this.practiceId = "";
      this.practiceGroups = { covered: [], notCovered: [] };
      this.practiceQuery = "";
      this.token = "";
      this.validationResult = null;
      this.errorCode = null;
    },

    /** @method clearResult - Solo limpia el resultado para reintentar */
    clearResult() {
      this.token = "";
      this.validationResult = null;
      this.errorCode = null;
    },

    /** @method getPatientSummary */
    getPatientSummary(member) {
      const parts = [];
      if (member.dni) parts.push(`DNI ${member.dni}`);
      if (member.memberNumber) parts.push(`Afil. ${member.memberNumber}`);
      if (member.plan?.name) parts.push(member.plan.name);
      return parts.join(" · ");
    },

    /** @method getInitials */
    getInitials(member) {
      return (
        (member.firstName || "")[0] + (member.lastName || "")[0]
      ).toUpperCase();
    },

    /** @method formatCurrency */
    formatCurrency(n) {
      return n != null ? `$${Number(n).toLocaleString("es-AR")}` : "-";
    },
  },
};
</script>

<template lang="pug">
  .validation-page
    .validation-card

      //- Steps
      .steps
        .step(:class="{ 'step--done': currentStep > 1, 'step--active': currentStep === 1 }")
          .step__number
            span(v-if="currentStep > 1") ✓
            span(v-else) 1
          span.step__label Afiliado
        .step__line(:class="{ 'step__line--done': currentStep > 1 }")
        .step(:class="{ 'step--done': currentStep > 2, 'step--active': currentStep === 2 }")
          .step__number
            span(v-if="currentStep > 2") ✓
            span(v-else) 2
          span.step__label Prestación
        .step__line(:class="{ 'step__line--done': currentStep > 2 }")
        .step(:class="{ 'step--active': currentStep === 3 }")
          .step__number 3
          span.step__label Validar

      //- Paso 1: Buscar afiliado
      h3.section-title Buscar afiliado
      .search-row
        el-input.search-field(
          v-model="searchInput"
          prefix-icon="el-icon-search"
          placeholder="Buscar por Nº de afiliado o DNI"
          clearable
          @keyup.enter.native="searchPatient"
        )
        el-button(type="primary" @click="searchPatient" :loading="searchingPatient") Buscar
        el-button(@click="clearAll") Limpiar

      //- Lista de resultados
      .patients-list(v-if="patientFound && patientFound.length && !patientSelected")
        .patient-item(
          v-for="(p, i) in patientFound"
          :key="p._id || i"
          @click="selectPatient(p)"
        )
          .patient-item__avatar {{ getInitials(p) }}
          .patient-item__info
            .patient-item__name {{ p.lastName }}, {{ p.firstName }}
            .patient-item__details {{ getPatientSummary(p) }}
          .patient-item__check

      //- Error afiliado
      .error-banner(v-if="errorPatient")
        i.el-icon-warning-outline
        span {{ errorPatient }}

      //- Afiliado seleccionado
      .selected-patient(v-if="patientSelected")
        .selected-patient__avatar {{ patientInitials }}
        .selected-patient__info
          span.selected-patient__name {{ patientName }}
          span.selected-patient__details {{ patientSummary }}
        button.selected-patient__clear(@click="clearAll")
          i.el-icon-close

      //- Paso 2 y 3: Prestación + Token
      template(v-if="patientSelected")
        .section-divider
        h3.section-title Prestación y validación

        el-form(label-position="top")
          el-form-item(label="Prestación")
            el-select(
              ref="practiceSelect"
              v-model="practiceId"
              filterable
              placeholder="Buscar por código o nombre..."
              :filter-method="filterPractices"
              :loading="practiceSearchLoading"
              popper-class="validation-practice-popper"
              :popper-append-to-body="true"
              :popper-options="practicePopperOptions"
              style="width:100%"
              @change="clearResult"
              @visible-change="onPracticeDropdownVisibleChange"
            )
              //- UC-REG-165 — Layout homogéneo (todas las filas mismo shape):
              //-   grid 3 columnas [Prestador · Financiador · Denominación].
              //- Cubiertas (arriba) + No cubiertas (abajo, tachadas + motivo).
              //- Cuando no hay providerCode/Name, el "código prestador" y la
              //- "denominación" caen al del financiador — layout invariante.
              el-option-group(
                v-if="visiblePracticeGroups.covered.length"
                :label="'Cubiertas para este plan · ' + visiblePracticeGroups.covered.length"
              )
                el-option.pp-option(
                  v-for="o in visiblePracticeGroups.covered"
                  :key="o.value"
                  :label="o.label"
                  :value="o.value"
                )
                  .pp-row
                    span.pp-code-prov {{ o.providerCode }}
                    span.pp-code-fin {{ o.financiadorCode }}
                    span.pp-name {{ o.providerName }}
              el-option-group(
                v-if="visiblePracticeGroups.notCovered.length"
                :label="'No cubiertas para este plan · ' + visiblePracticeGroups.notCovered.length"
              )
                el-option.pp-option.pp-option--dis(
                  v-for="o in visiblePracticeGroups.notCovered"
                  :key="o.value"
                  :label="o.label"
                  :value="o.value"
                  :disabled="true"
                )
                  .pp-row
                    span.pp-code-prov {{ o.providerCode }}
                    span.pp-code-fin {{ o.financiadorCode }}
                    span.pp-name {{ o.providerName }}
                  .pp-reason(v-if="o.disabledReason") {{ o.disabledReason }}

        .token-row(v-if="practiceId")
          .token-field
            label.form-label-inline Token
            el-input.token-input(
              v-model="token"
              placeholder="_ _ _ _ _ _"
              clearable
              maxlength="6"
              @keyup.enter.native="validateCode"
            )
          el-button(
            type="primary"
            @click="validateCode"
            :loading="validatingCode"
            :disabled="!token || !practiceId"
            style="height:40px; padding: 0 24px"
          ) Validar
          el-button(
            @click="clearResult"
            :disabled="!token"
            style="height:40px"
          ) Limpiar

      //- Resultado aprobado
      .result-banner.result-banner--success(v-if="validationResult && validationResult.approved")
        .result-banner__icon.result-banner__icon--success ✓
        .result-banner__content
          .result-banner__title.result-banner__title--success Prestación autorizada
          .result-checks
            span.result-check.result-check--valid
              span.dot.dot--valid
              | Afiliado activo
            span.result-check.result-check--valid
              span.dot.dot--valid
              | Plan vigente
            span.result-check.result-check--valid
              span.dot.dot--valid
              | Token válido
            span.result-check.result-check--valid
              span.dot.dot--valid
              | Prestación cubierta
            span.result-check.result-check--valid
              span.dot.dot--valid
              | Prestador habilitado
          //- Valorización — se oculta el "Valor de la prestación" (monto de
          //- la transacción) porque el prestador no necesita verlo al validar;
          //- ya aparece en el historial. Se mantiene copago/coseguro porque
          //- son datos operacionales que el prestador SÍ usa al cobrar.
          .valorization(v-if="validationResult.valorization && (validationResult.valorization.hasCopago || validationResult.valorization.hasCoseguro)")
            .valorization__item(v-if="validationResult.valorization.hasCopago")
              span.valorization__label Copago del paciente
              span.valorization__amount {{ formatCurrency(validationResult.valorization.copagoAmount) }}
            .valorization__item(v-if="validationResult.valorization.hasCoseguro")
              span.valorization__label Coseguro
              span.valorization__amount {{ validationResult.valorization.coseguroPercent }}%
          el-button(type="text" style="margin-top:8px; color:#6b7294; font-size:12px" @click="clearAll") Nueva validación

      //- Resultado rechazado
      .result-banner.result-banner--error(v-if="validationResult && !validationResult.approved")
        .result-banner__icon.result-banner__icon--error ✕
        .result-banner__content
          .result-banner__title.result-banner__title--error Prestación no autorizada
          .result-checks
            .result-check(
              v-for="(label, i) in rejectionLabels"
              :key="i"
              class="result-check--invalid"
            )
              span.dot.dot--invalid
              | {{ label }}
          //- Mensaje del motor externo, textual: el validador muestra la
          //- respuesta del servicio sin parafrasear. Ausente con motor interno.
          .result-external-message(v-if="validationResult.externalMessage")
            | {{ validationResult.externalMessage }}
          el-button(type="text" style="margin-top:8px; color:#6b7294; font-size:12px" @click="clearResult") Reintentar

      //- Error general
      .result-banner.result-banner--error(v-if="errorCode")
        .result-banner__icon.result-banner__icon--error !
        .result-banner__content
          .result-banner__title.result-banner__title--error {{ errorCode }}
</template>

<style lang="scss" scoped>
// UC-REG-165 — Estilos del popper del dropdown de prestaciones.
// Usa `popper-class="validation-practice-popper"` sobre el-select y NO
// scoped (el popper se adjunta al body, fuera del árbol del componente).
// Los estilos van al bloque global de más abajo.
</style>

<!--
  UC-REG-165 — Bloque global (NO scoped) para el popper del validador.
  Element UI adjunta el .el-select-dropdown al <body>, por eso los
  estilos scoped no lo alcanzan. Se aíslan con la clase `validation-practice-popper`
  para no impactar otros el-select del dashboard.
-->
<style lang="scss">
.validation-practice-popper {
  min-width: 560px !important;

  .el-select-group__title {
    padding: 8px 16px;
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #94a3b8;
    font-weight: 500;
    background: #f8fafc;
    border-bottom: 0.5px solid #e2e8f0;
  }
  // La 2da sección (no cubiertas) va en tono rojo suave
  .el-select-group + .el-select-group .el-select-group__title {
    color: #b91c1c;
    background: #fef2f2;
  }

  .el-select-dropdown__item {
    height: auto;
    min-height: 44px;
    padding: 0;
    line-height: 1.35;
  }
  .el-select-dropdown__item.selected .pp-code-prov,
  .el-select-dropdown__item.selected .pp-name {
    color: #002d5e;
    font-weight: 500;
  }

  // ─── Fila (grid 3 columnas) ──────────────────────────────
  .pp-row {
    display: grid;
    grid-template-columns: 100px 100px 1fr;
    gap: 16px;
    align-items: center;
    padding: 10px 16px;
  }
  .pp-code-prov {
    font-variant-numeric: tabular-nums;
    font-size: 14px;
    font-weight: 500;
    color: #002d5e;
  }
  .pp-code-fin {
    font-variant-numeric: tabular-nums;
    font-size: 12px;
    padding: 3px 9px;
    border-radius: 5px;
    font-weight: 500;
    justify-self: start;
    white-space: nowrap;
    background: #ccfbf1;
    color: #115e59;
  }
  .pp-name {
    font-size: 13px;
    color: #1a1f36;
    line-height: 1.35;
    white-space: normal;
    overflow: visible;
    text-overflow: unset;
  }

  // ─── Deshabilitadas (no cubiertas para este plan) ────────
  .pp-option--dis {
    cursor: not-allowed !important;
    .pp-code-prov,
    .pp-name {
      color: #94a3b8;
      text-decoration: line-through;
      text-decoration-color: #cbd5e1;
    }
    .pp-code-fin {
      opacity: 0.5;
      text-decoration: line-through;
      text-decoration-color: #cbd5e1;
    }
  }
  .pp-reason {
    padding: 0 16px 8px;
    font-size: 11px;
    color: #dc2626;
    font-style: italic;
  }
}
</style>

<style lang="scss" scoped>
.validation-page {
  padding: 0;
}

.validation-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 28px 32px;
  margin-top: 16px;
}

.steps {
  display: flex;
  align-items: center;
  margin-bottom: 28px;
}
.step {
  display: flex;
  align-items: center;
  gap: 8px;
  &__number {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: #e2e8f0;
    color: #6b7294;
    font-family: "Plus Jakarta Sans", sans-serif;
    font-size: 13px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  &__label {
    font-family: "Plus Jakarta Sans", sans-serif;
    font-size: 13px;
    font-weight: 600;
    color: #94a3b8;
  }
  &--active &__number {
    background: #002d5e;
    color: #fff;
  }
  &--active &__label {
    color: #002d5e;
  }
  &--done &__number {
    background: #05e5ba;
    color: #002d5e;
  }
  &--done &__label {
    color: #002d5e;
  }
  &__line {
    flex: 1;
    height: 2px;
    background: #e2e8f0;
    margin: 0 12px;
    &--done {
      background: #05e5ba;
    }
  }
}

.section-title {
  font-family: "Plus Jakarta Sans", sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: #002d5e;
  margin: 0 0 16px;
}
.section-divider {
  height: 1px;
  background: #e2e8f0;
  margin: 24px 0;
}

.search-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
}
.search-field {
  flex: 1;
}

::v-deep .el-input__inner {
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  font-family: "Plus Jakarta Sans", sans-serif;
  font-size: 13px;
  height: 40px;
  &:focus {
    border-color: #002d5e;
  }
  &::placeholder {
    color: #94a3b8;
  }
}

// Botones Element UI redondeados (8px) — homogeneiza con el resto del producto.
::v-deep .el-button {
  border-radius: 8px;
}
::v-deep .el-select .el-input__inner {
  border-radius: 8px;
}

.form-label-inline {
  font-family: "Plus Jakarta Sans", sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: #6b7294;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 6px;
  display: block;
}

.patients-list {
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 6px;
  margin-bottom: 14px;
  max-height: 200px;
  overflow-y: auto;
}
.patient-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.15s;
  border: 1px solid transparent;
  &:hover {
    background: #f8fafc;
  }
  &__avatar {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: #002d5e;
    color: #fff;
    font-family: "Plus Jakarta Sans", sans-serif;
    font-size: 12px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }
  &__info {
    flex: 1;
    min-width: 0;
  }
  &__name {
    font-family: "Plus Jakarta Sans", sans-serif;
    font-size: 13px;
    font-weight: 600;
    color: #002d5e;
  }
  &__details {
    font-family: "Plus Jakarta Sans", sans-serif;
    font-size: 11px;
    color: #6b7294;
    margin-top: 1px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  &__check {
    width: 18px;
    height: 18px;
    border: 2px solid #cbd5e1;
    border-radius: 50%;
    flex-shrink: 0;
  }
}

.selected-patient {
  display: flex;
  align-items: center;
  gap: 12px;
  background: #e8fdf8;
  border: 1px solid #05e5ba;
  border-radius: 10px;
  padding: 12px 16px;
  &__avatar {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: #002d5e;
    color: #fff;
    font-family: "Plus Jakarta Sans", sans-serif;
    font-size: 13px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }
  &__info {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
  }
  &__name {
    font-family: "Plus Jakarta Sans", sans-serif;
    font-size: 14px;
    font-weight: 600;
    color: #002d5e;
  }
  &__details {
    font-family: "Plus Jakarta Sans", sans-serif;
    font-size: 12px;
    color: #6b7294;
    margin-top: 1px;
  }
  &__clear {
    background: none;
    border: none;
    color: #94a3b8;
    cursor: pointer;
    padding: 4px 6px;
    border-radius: 4px;
    &:hover {
      color: #002d5e;
      background: #f0f2f7;
    }
    i {
      font-size: 16px;
    }
  }
}

.token-row {
  display: flex;
  gap: 12px;
  align-items: flex-end;
  margin-top: 8px;
}
.token-field {
  flex: 1;
}
.token-input {
  ::v-deep .el-input__inner {
    font-size: 20px;
    font-weight: 700;
    letter-spacing: 8px;
    text-align: center;
    color: #002d5e;
  }
}

.error-banner {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 10px;
  padding: 12px 16px;
  margin-bottom: 14px;
  font-family: "Plus Jakarta Sans", sans-serif;
  font-size: 13px;
  color: #991b1b;
  i {
    font-size: 18px;
    color: #dc2626;
    flex-shrink: 0;
  }
}

.result-banner {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px 20px;
  border-radius: 10px;
  margin-top: 16px;
  &--success {
    background: #f0fdf4;
    border: 1px solid #bbf7d0;
  }
  &--error {
    background: #fef2f2;
    border: 1px solid #fecaca;
  }
  &__icon {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    font-weight: 700;
    flex-shrink: 0;
    &--success {
      background: #dcfce7;
      color: #16a34a;
    }
    &--error {
      background: #fee2e2;
      color: #dc2626;
    }
  }
  &__content {
    flex: 1;
  }
  &__title {
    font-family: "Plus Jakarta Sans", sans-serif;
    font-size: 15px;
    font-weight: 700;
    margin-bottom: 8px;
    &--success {
      color: #166534;
    }
    &--error {
      color: #991b1b;
    }
  }
}

.result-checks {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.result-external-message {
  margin-top: 8px;
  padding: 8px 10px;
  border-radius: 6px;
  background: rgba(0, 0, 0, 0.04);
  font-family: "Plus Jakarta Sans", sans-serif;
  font-size: 12px;
  color: #6b7294;
  font-style: italic;
}
.result-check {
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: "Plus Jakarta Sans", sans-serif;
  font-size: 13px;
  font-weight: 500;
  &--valid {
    color: #16a34a;
  }
  &--invalid {
    color: #dc2626;
  }
}
.dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex-shrink: 0;
  &--valid {
    background: #16a34a;
  }
  &--invalid {
    background: #dc2626;
  }
}

.valorization {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #bbf7d0;
  display: flex;
  flex-direction: column;
  gap: 4px;
  &__item {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  &__label {
    font-family: "Plus Jakarta Sans", sans-serif;
    font-size: 12px;
    color: #166534;
  }
  &__amount {
    font-family: "Plus Jakarta Sans", sans-serif;
    font-size: 14px;
    font-weight: 700;
    color: #166534;
  }
}

@media (max-width: 768px) {
  .search-row {
    flex-wrap: wrap;
  }
  .token-row {
    flex-wrap: wrap;
  }
  .result-checks {
    flex-wrap: wrap;
    gap: 8px;
  }
}
</style>
